/**
 * Pear API + session storage.
 *
 * Lifted verbatim from `main.ts` (Sprint 1) so the React shell can share one
 * implementation with the panels. Same endpoints, same localStorage keys —
 * an existing session on a device stays valid across the migration.
 */

/** Empty in dev (vite proxies /api and /ws); set at build time for Pages. */
export const API_BASE: string =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env
    ?.VITE_API_BASE) || '';

export type Role = 'A' | 'B';

export interface Member {
  memberId: string;
  displayName: string;
  role: Role;
}

export interface PairResult {
  token: string;
  role: Role;
  memberId: string;
  couple: { coupleId: string; code?: string };
}

const LS_TOKEN = 'pear_token';
const LS_COUPLE_ID = 'pear_couple_id';
const LS_ROLE = 'pear_role';
const LS_MEMBER_ID = 'pear_member_id';
const LS_MEMBERS_JSON = 'pear_members_json';

export function getToken(): string | null {
  return localStorage.getItem(LS_TOKEN);
}
export function getMemberId(): string | null {
  return localStorage.getItem(LS_MEMBER_ID);
}
export function saveMembers(members: Member[]) {
  localStorage.setItem(LS_MEMBERS_JSON, JSON.stringify(members));
}
export function loadMembers(): Member[] {
  const raw = localStorage.getItem(LS_MEMBERS_JSON) ?? '';
  if (!raw) return [];
  try {
    return JSON.parse(raw) as Member[];
  } catch {
    return [];
  }
}
export function saveSession(token: string, coupleId: string, role: Role, memberId: string) {
  localStorage.setItem(LS_TOKEN, token);
  localStorage.setItem(LS_COUPLE_ID, coupleId);
  localStorage.setItem(LS_ROLE, role);
  localStorage.setItem(LS_MEMBER_ID, memberId);
}
export function clearSession() {
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_COUPLE_ID);
  localStorage.removeItem(LS_ROLE);
  localStorage.removeItem(LS_MEMBER_ID);
  localStorage.removeItem(LS_MEMBERS_JSON);
}
export function sessionIsRestorable(): boolean {
  return Boolean(getToken() && localStorage.getItem(LS_COUPLE_ID) && localStorage.getItem(LS_ROLE));
}

export async function api<T = any>(
  path: string,
  opts?: { body?: Record<string, unknown>; headers?: Record<string, string> },
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(opts?.headers ?? {}) };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(API_BASE + path, {
    method: opts?.body ? 'POST' : 'GET',
    headers,
    body: opts?.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(JSON.stringify(data));
  return data as T;
}

/** WebSocket endpoint that matches API_BASE (http→ws, same host in dev). */
export function wsUrl(token: string): string {
  const wsBase = API_BASE
    ? API_BASE.replace(/^http/, 'ws')
    : `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}`;
  return `${wsBase}/ws?token=${encodeURIComponent(token)}`;
}
